/**
 * Parses validation errors returned by the API into a single shape the forms
 * can render.
 *
 * The server sends a rich envelope for validation failures:
 *
 *   {
 *     error: 'VALIDATION_ERROR',
 *     message: 'We could not submit your onboarding form because 3 fields ...',
 *     error_count: 3,
 *     errors: [{ field, form_field, label, section, section_label, code,
 *                message, hint, example, received, index }],
 *     field_errors: { gstin: ['GSTIN must be exactly 15 characters ...'] },
 *     section_errors: { legal_financial: [ ...issues ] },
 *     sections: ['legal_financial'],
 *   }
 *
 * Older responses used per-section keys (`brand_errors`,
 * `contact_information_errors`, …) or a bare `{ detail }` / `{ error }`, so
 * those are handled too — an endpoint that has not been updated still produces
 * something readable instead of "HTTP 400: Bad Request".
 */

import { isServerBusyError, HIGH_TRAFFIC_MESSAGE } from './serverBusy';

// Section keys the API used before the shared envelope existed.
const LEGACY_SECTION_KEYS = {
  brand_errors: 'brand',
  contact_information_errors: 'contact_information',
  legal_financial_errors: 'legal_financial',
  manufacturing_errors: 'manufacturing',
  order_policy_errors: 'order_policy',
  exchange_policy_errors: 'exchange_policy',
  return_policy_errors: 'return_policy',
  product_categorization_errors: 'product_categorization',
  warehouses_errors: 'warehouses',
};

const SECTION_LABELS = {
  brand: 'Brand & Contact Information',
  contact_information: 'Company Information',
  legal_financial: 'Legal & Financial',
  manufacturing: 'Manufacturing',
  order_policy: 'Business Policies',
  exchange_policy: 'Business Policies',
  return_policy: 'Business Policies',
  product_categorization: 'Product Categorization',
  warehouses: 'Warehouse Information',
  terms: 'Terms & Conditions',
  signup: 'Sign Up',
};

/** Turn `brand_poc_number` into `Brand Poc Number` when the API sent no label. */
export function humanizeFieldName(fieldName) {
  if (!fieldName) return 'This field';
  return String(fieldName)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Key used to de-duplicate a field+message pair.
 *
 * The separator is an explicit \u0000 escape rather than a literal NUL byte:
 * it cannot appear in a field name or a message, so `a` + `b|c` and `a|b` + `c`
 * can never collide, and the source stays plain text.
 */
function issueKey(field, message) {
  return `${field}\u0000${message}`;
}

function toMessageList(value) {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.flatMap(toMessageList);
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nested]) =>
      toMessageList(nested).map((msg) => (key === 'non_field_errors' ? msg : `${humanizeFieldName(key)}: ${msg}`))
    );
  }
  return [String(value)];
}

/**
 * Read the body out of whatever the caller caught.
 *
 * `authenticatedFetch` throws an Error carrying `data` (the parsed body) and
 * `status`. Some callers hand us the body directly, and axios-style callers use
 * `response.data`, so all three are accepted.
 */
export function extractErrorBody(err) {
  if (!err) return null;
  if (err.data && typeof err.data === 'object') return err.data;
  if (err.response && err.response.data && typeof err.response.data === 'object') {
    return err.response.data;
  }
  if (typeof err === 'object' && !(err instanceof Error)) return err;
  return null;
}

/**
 * Normalize an error body into `{ message, count, issues, fieldErrors, sections }`.
 *
 * - `issues`  — ordered list of `{ field, label, section, sectionLabel, message, hint, example, received, index }`
 * - `fieldErrors` — `{ formFieldName: 'first message' }`, ready to bind to inputs
 * - `sections` — `[{ key, label, issues }]` for grouping in a summary banner
 */
export function parseValidationErrors(err, fallbackMessage = 'Something went wrong. Please try again.') {
  // An overloaded backend (502/503/504, or a fetch the browser abandoned) is not
  // a validation failure and not the user's fault. Say that plainly and point at
  // a retry, instead of surfacing "Failed to fetch" or a bare status line.
  if (isServerBusyError(err)) {
    return {
      message: HIGH_TRAFFIC_MESSAGE,
      count: 0,
      issues: [],
      fieldErrors: {},
      sections: [],
      isValidationError: false,
      isServerBusy: true,
    };
  }

  const body = extractErrorBody(err);

  if (!body) {
    return {
      message: (err && err.message) || fallbackMessage,
      count: 0,
      issues: [],
      fieldErrors: {},
      sections: [],
      isValidationError: false,
    };
  }

  const issues = [];

  // 1. Preferred: the rich envelope.
  if (Array.isArray(body.errors)) {
    body.errors.forEach((issue) => {
      if (!issue) return;
      // A plain string in `errors` is not a field problem, just a message.
      if (typeof issue === 'string') {
        issues.push({ field: '', label: '', message: issue });
        return;
      }
      const field = issue.form_field || issue.field || '';
      issues.push({
        field,
        label: issue.label || humanizeFieldName(field),
        section: issue.section || '',
        sectionLabel: issue.section_label || SECTION_LABELS[issue.section] || '',
        code: issue.code || '',
        message: issue.message || '',
        hint: issue.hint || '',
        example: issue.example || '',
        received: issue.received,
        index: typeof issue.index === 'number' ? issue.index : null,
      });
    });
  } else if (body.errors && typeof body.errors === 'object') {
    // `errors` as a `{ field: [messages] }` map (older submit responses).
    Object.entries(body.errors).forEach(([field, value]) => {
      toMessageList(value).forEach((message) => {
        issues.push({ field, label: humanizeFieldName(field), message, section: '', sectionLabel: '' });
      });
    });
  }

  // 2. `field_errors` fills in anything the list missed. This is checked per
  // message rather than only when `errors` is empty: a partial `errors` list
  // would otherwise silently drop the messages it did not include.
  if (body.field_errors && typeof body.field_errors === 'object') {
    const seen = new Set(issues.map((issue) => issueKey(issue.field, issue.message)));
    Object.entries(body.field_errors).forEach(([field, value]) => {
      toMessageList(value).forEach((message) => {
        const key = issueKey(field, message);
        if (seen.has(key)) return;
        seen.add(key);
        issues.push({ field, label: humanizeFieldName(field), message, section: '', sectionLabel: '' });
      });
    });
  }

  // 3. Legacy per-section keys.
  if (issues.length === 0) {
    Object.entries(LEGACY_SECTION_KEYS).forEach(([key, section]) => {
      const sectionErrors = body[key];
      if (!sectionErrors) return;

      if (Array.isArray(sectionErrors)) {
        // Warehouses: one entry per row, `null` where the row was fine.
        sectionErrors.forEach((rowErrors, index) => {
          if (!rowErrors) return;
          if (typeof rowErrors === 'string') {
            issues.push({ field: '', label: '', message: rowErrors, section, sectionLabel: SECTION_LABELS[section], index });
            return;
          }
          Object.entries(rowErrors).forEach(([field, value]) => {
            toMessageList(value).forEach((message) => {
              issues.push({
                field,
                label: humanizeFieldName(field),
                message,
                section,
                sectionLabel: SECTION_LABELS[section],
                index,
              });
            });
          });
        });
      } else if (typeof sectionErrors === 'object') {
        Object.entries(sectionErrors).forEach(([field, value]) => {
          toMessageList(value).forEach((message) => {
            issues.push({
              field,
              label: humanizeFieldName(field),
              message,
              section,
              sectionLabel: SECTION_LABELS[section],
            });
          });
        });
      } else {
        issues.push({ field: '', label: '', message: String(sectionErrors), section, sectionLabel: SECTION_LABELS[section] });
      }
    });
  }

  // 4. Bare `{ field: [messages] }` bodies (e.g. plain DRF serializer errors).
  if (issues.length === 0) {
    const skipKeys = new Set([
      'error', 'message', 'detail', 'error_count', 'errors', 'field_errors',
      'section_errors', 'sections', 'status', 'code', 'field',
    ]);
    Object.entries(body).forEach(([field, value]) => {
      if (skipKeys.has(field)) return;
      toMessageList(value).forEach((message) => {
        issues.push({ field, label: humanizeFieldName(field), message, section: '', sectionLabel: '' });
      });
    });
  }

  // Build the flat map the inputs bind to. First message per field wins, so the
  // most specific problem is the one shown under the input.
  const fieldErrors = {};
  issues.forEach((issue) => {
    if (issue.field && !fieldErrors[issue.field]) {
      fieldErrors[issue.field] = issue.message;
    }
  });

  // Group for the summary banner, preserving the order the server sent.
  // Grouping is by the heading shown on screen rather than the server's section
  // key: one API section can span two headings in the form (contact details are
  // split across "Brand & Contact Information" and "Company Information"), and
  // the banner has to match what the reader is looking at.
  const sectionOrder = [];
  const sectionMap = {};
  issues.forEach((issue) => {
    const label = issue.sectionLabel || SECTION_LABELS[issue.section] || '';
    const key = label || issue.section || 'general';
    if (!sectionMap[key]) {
      sectionMap[key] = { key, label, issues: [] };
      sectionOrder.push(key);
    }
    sectionMap[key].issues.push(issue);
  });

  let message = body.message || body.detail || '';
  // `error` is a machine code in the new envelope; only show it if it reads
  // like a sentence rather than a code such as VALIDATION_ERROR.
  if (!message && typeof body.error === 'string' && body.error.includes(' ')) {
    message = body.error;
  }
  if (!message) {
    message = issues.length
      ? `Please fix the ${issues.length} highlighted ${issues.length === 1 ? 'field' : 'fields'} and try again.`
      : (err && err.message) || fallbackMessage;
  }

  return {
    message,
    count: typeof body.error_count === 'number' ? body.error_count : issues.length,
    issues,
    fieldErrors,
    sections: sectionOrder.map((key) => sectionMap[key]),
    isValidationError: issues.length > 0 || body.error === 'VALIDATION_ERROR',
  };
}

/**
 * A one-line summary suitable for a toast.
 */
export function summarizeValidationErrors(parsed) {
  if (!parsed) return '';
  if (parsed.count > 1 && parsed.issues.length) {
    const names = parsed.issues.slice(0, 3).map((i) => i.label).filter(Boolean);
    const more = parsed.count - names.length;
    const list = names.join(', ');
    return more > 0
      ? `${parsed.count} fields need attention: ${list} and ${more} more.`
      : `${parsed.count} fields need attention: ${list}.`;
  }
  if (parsed.issues.length === 1) {
    const issue = parsed.issues[0];
    return issue.label ? `${issue.label}: ${issue.message}` : issue.message;
  }
  return parsed.message;
}

export default parseValidationErrors;
